import { useRef } from 'react';
import { ArrowUpDown, Check, ChevronDown, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { SORT_OPTIONS, sortLabel, STATUS_TABS } from './projectsModel';

const tabSlug = (key) => (key === 'all' ? 'all' : key.toLowerCase().replace(/\s+/g, '-'));

/** DOM id of a status tab — the list panel points back at the selected one (aria-labelledby). */
export const statusTabId = (idPrefix, key) => `${idPrefix}-tab-${tabSlug(key)}`;

/**
 * All 63 · Active 45 · On hold 4 · Completed 14 — status as tabs with live counts (WAI-ARIA tabs:
 * ←/→/Home/End move between them). Every tab controls the one list panel (`panelId`).
 */
export function StatusTabs({ value, counts, onChange, idPrefix, panelId, className }) {
  const listRef = useRef(null);

  const focusTab = (index) => {
    const buttons = listRef.current?.querySelectorAll('[role="tab"]');
    if (!buttons?.length) return;
    const next = (index + buttons.length) % buttons.length;
    buttons[next].focus();
    onChange(STATUS_TABS[next].key);
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label="Project status"
      data-testid="status-filter"
      className={cn('-mb-px flex min-w-0 max-w-full gap-4 overflow-x-auto sm:gap-[22px]', className)}
    >
      {STATUS_TABS.map((tab, index) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={idPrefix ? statusTabId(idPrefix, tab.key) : undefined}
            aria-controls={panelId}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            data-testid={`projects-tab-${tabSlug(tab.key)}`}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') { event.preventDefault(); focusTab(index + 1); }
              if (event.key === 'ArrowLeft') { event.preventDefault(); focusTab(index - 1); }
              if (event.key === 'Home') { event.preventDefault(); focusTab(0); }
              if (event.key === 'End') { event.preventDefault(); focusTab(STATUS_TABS.length - 1); }
            }}
            className={cn(
              // Phones: a 44px touch target (the mobile spec).
              'relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap pb-[11px] pt-2 text-[13.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:min-h-11',
              'after:absolute after:inset-x-0 after:bottom-0 after:h-[2.5px] after:rounded-t',
              active ? 'text-ink after:bg-brand' : 'text-ink-3 after:bg-transparent hover:text-ink-2'
            )}
          >
            {tab.label}
            <span
              className={cn(
                'rounded-full px-[7px] py-px text-[11px] font-bold tabular-nums',
                active ? 'bg-brand text-brand-fg' : 'bg-[color:var(--status-gray-bg)] text-ink-2'
              )}
            >
              {counts?.[tab.key] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Everyone | Mine — "Mine" = projects where I am the Project Engineer or Senior Project Engineer. */
export function ScopeToggle({ value, onChange, disabled }) {
  const options = [
    { key: 'everyone', label: 'Everyone' },
    { key: 'mine', label: 'Mine', icon: UserRound },
  ];
  return (
    <div
      role="group"
      aria-label="Whose projects"
      className="inline-flex shrink-0 gap-0.5 rounded-[11px] bg-[color:var(--status-gray-bg)] p-[3px]"
    >
      {options.map((option) => {
        const active = option.key === value;
        const Icon = option.icon;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            disabled={disabled && option.key === 'mine'}
            data-testid={`projects-scope-${option.key}`}
            onClick={() => onChange(option.key)}
            title={option.key === 'mine' ? 'Projects where you are the Project Engineer or Senior Project Engineer' : undefined}
            className={cn(
              // 28px on desktop; 44px touch targets on phones (like the List | Map switch).
              'inline-flex h-[28px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-sm:h-11 max-sm:px-3',
              active ? 'bg-surface text-ink shadow-card' : 'text-ink-2 hover:text-ink'
            )}
          >
            {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * "Sort: Needs attention ⌄" — the list order (also changed by clicking a column header). A header click
 * can give an order the menu does not list (e.g. 'name:desc'): it is named ("Project Z–A") and shown
 * checked at the end of the menu.
 */
export function SortMenu({ value, onChange, compact = false }) {
  const label = sortLabel(value);
  const listed = SORT_OPTIONS.some((option) => option.value === value);
  const options = listed || label === 'Custom' ? SORT_OPTIONS : [...SORT_OPTIONS, { value, label }];
  const current = options.find((option) => option.value === value);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="shrink-0 gap-1.5 px-2 text-ink-2" data-testid="projects-sort">
          <ArrowUpDown className="text-ink-3" aria-hidden="true" />
          {compact ? 'Sort' : <span>Sort: <b className="font-semibold text-ink">{label}</b></span>}
          {!compact ? <ChevronDown className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" /> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Sort by</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={current ? value : ''} onValueChange={onChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value} className="h-8 rounded-lg pl-8 text-[13px]">
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "Group by site" switch (03 #7). */
export function GroupToggle({ checked, onChange }) {
  return (
    <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap text-[13px] font-semibold text-ink-2">
      Group by site
      <Switch
        checked={checked}
        onCheckedChange={(next) => onChange(Boolean(next))}
        aria-label="Group by site"
        className="data-[state=checked]:bg-status-green"
        data-testid="projects-group-toggle"
      />
    </label>
  );
}

/**
 * Phones: the attention shortcuts as three tiles (03 #6 mobile). Each one filters the list in one tap;
 * the active one is outlined.
 */
export function AttentionTiles({ items, value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2" role="group" aria-label="Needs attention">
      {items.map((item) => {
        const active = value === item.value;
        return (
          <button
            key={item.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? '' : item.value)}
            className={cn(
              'flex min-h-[58px] min-w-0 flex-col justify-center gap-px rounded-[14px] border bg-surface px-2.5 py-2 text-left shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active ? 'border-[color:var(--brand-soft-border)] bg-brand-soft' : 'border-line'
            )}
            data-testid={`projects-attention-${item.value}`}
          >
            <span className={cn('font-heading text-[18px] font-black leading-[1.1]', item.tone === 'red' ? 'text-status-red-ink' : 'text-status-amber-ink', !item.count && 'text-ink-3')}>
              {formatNumber(item.count, { maxDecimals: 0 })}
            </span>
            <span className="flex items-start gap-1 text-[11.5px] font-semibold leading-tight text-ink-2">
              {active ? <Check className="mt-px h-3 w-3 shrink-0" aria-hidden="true" /> : null}
              {item.short}
            </span>
          </button>
        );
      })}
    </div>
  );
}
